// "Hold the bags" frame (sprint 36 item 3), drawn only from the Aseprite exports in client/art/aseprite/holden-game/
// (art/aseprite/src/holden_game.lua, HOLDEN-ART.md). This file chooses which frame of which sheet goes where on each 1/15 s step;
// it draws no shape of its own. holdenScene() is pure (tested in test/holden-client.test.js); drawHolden() copies its list onto the canvas.
//
// Reading the game's observation (game/minigames/holden.js observe()):
//  - the hook swings on its rope: the rope frame for the hook's offset, the hook, the bag under it (swing-left / swing-right by the
//    direction it moves, hang at the ends); the pile in his arms is every landed bag, each centred where it landed;
//  - a drop falls in 3 stepped frames from the hook to the pile (the game lands it at once); a bag that landed off the bag below droops
//    on that side and a can, a loaf or a scrap falls off the overhang;
//  - Mr Holden: strain for 2 steps after a landing, wobble (to the side of a big overhang) for 4, palms-up when the pile tips, win at the bell;
//  - the line is drawn at bag 6's height in both modes; when bag 6 lands "LINE!" stamps in beside the pile;
//  - the end: eight bags and "STILL HOLDING!" (the stamp-in frame, then settled), or the pile tipping: the last three bags go over in the
//    tip sprite (leaning, sliding, the heap in the snow) while the bags below stay.
// Reduced motion: a dropped bag lands at once and the bits do not fall; everything else is already a stepped frame.
import { sheet, blit, frameOf } from './minigame-art.js';

const PIECE = 'holden-game';
export const SHEETS = { night: 'holden-game-night', man: 'holden-game-man', eyes: 'holden-game-eyes', paper: 'holden-game-paper', tote: 'holden-game-tote', sack: 'holden-game-sack',
  rope: 'holden-game-rope', hook: 'holden-game-hook', bits: 'holden-game-bits', tip: 'holden-game-tip', line: 'holden-game-line', linestamp: 'holden-game-linestamp', still: 'holden-game-still' };
export const ART = Object.fromEntries(Object.entries(SHEETS).map(([k, name]) => [k, sheet(PIECE, name, { still: ['night', 'hook', 'line'].includes(k) })]));

// Anchors written by holden_game.lua (holden-game-anchors.json); these are the values it writes, checked by the test.
export const ANCHORS = {
  layout: { ground: 272, hookY: 60, man: [66, 212], pile: [90, 213], pulley: [90, 12] },
  man: { eyes: { idle: [20, 23], 'palms-up': [20, 23], strain: [20, 26], win: [20, 23], 'wobble-left': [16, 23], 'wobble-right': [24, 23] } },
  bags: { paper: { hook: 11, land: 14, size: [22, 18] }, tote: { hook: 8, land: 11, size: [16, 18] }, sack: { hook: 14, land: 26, size: [28, 28] } },
  hook: { holds: [3, 9], size: [7, 10] },
  rope: { offsets: [-24, 24], top: [25, 0] },
  tip: { foot: [36, 64], size: [72, 64] },
  line: { rule: 3, x: 8 },
  still: { size: [148, 86] },
  linestamp: { size: [59, 37] },
};
const L = ANCHORS.layout, STEPS = 120;
const FALL = 3; // steps a dropped bag takes to reach the pile
const HEAP_SIDE = 48; // px the tipped heap's middle lies to the side of the pile's, so the heap is beside Mr Holden
const BITS = ['can', 'loaf', 'scrap'];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// the bottom row of the bag after `n` landed bags: the pile's foot minus the rows each added
export const bottomAfter = (landed, n) => landed.slice(0, n).reduce((y, b) => y - ANCHORS.bags[b.kind].land, L.pile[1]);

// Mr Holden's pose and the eyes over it. tell: the eyes at the end ('human' by default; the reveal's eyes could be passed in).
export function manPose(o, after = 0, tell = 'human') {
  if (o.tip) return { pose: 'palms-up', eyes: tell };
  if (o.t >= STEPS) return after >= 1 ? { pose: 'win', eyes: tell } : { pose: 'idle', eyes: 'open' };
  const last = o.landed.at(-1);
  if (last) {
    const since = o.t - 1 - last.step;
    if (since >= 0 && since < 4 && last.off * 4 > last.w) return { pose: last.side < 0 ? 'wobble-left' : 'wobble-right', eyes: 'squint' };
    if (since >= 0 && since < 2) return { pose: 'strain', eyes: 'squint' };
  }
  return { pose: 'idle', eyes: 'open' };
}

// The whole frame as a list of copies: { art, frame, x, y }, bottom first. frame is [tag, k] or an index.
export function holdenScene(o, { reduced = false, after = 0, tell = 'human' } = {}) {
  const now = o.t + after; // the steps keep counting after the end, so the last drop still falls and the stamps still settle
  const ops = [], put = (art, frame, x, y) => ops.push({ art, frame, x: Math.round(x), y: Math.round(y) });
  const standing = o.tip ? Math.max(0, o.landed.length - 3) : o.landed.length;
  const lineY = L.pile[1] - o.lineRows;
  put('night', 0, 0, 0);
  put('line', 0, ANCHORS.line.x, lineY - ANCHORS.line.rule);
  // the pile: each landed bag centred where it landed, resting on the one below; a dropped bag still on its way down
  const bits = [];
  o.landed.slice(0, standing).forEach((b, i) => {
    const A = ANCHORS.bags[b.kind], bottom = bottomAfter(o.landed, i), finalTop = bottom - A.size[1];
    const since = now - 1 - b.step, falling = !reduced && since >= 0 && since < FALL - 1;
    const startTop = L.hookY + ANCHORS.hook.holds[1], top = falling ? startTop + Math.round(((finalTop - startTop) * (since + 1)) / FALL) : finalTop;
    put(b.kind, [falling ? 'hang' : b.off > 0 ? (b.side < 0 ? 'droop-left' : 'droop-right') : 'land', 0], L.pile[0] + b.c - A.hook, top);
    if (b.off > 0 && since >= 0 && since < 8) bits.push({ b, since, top: finalTop, i });
  });
  // Mr Holden over the foot of the pile, and his eyes
  const mp = manPose(o, after, tell), [mx, my] = L.man, ea = ANCHORS.man.eyes[mp.pose];
  put('man', [mp.pose, 0], mx, my);
  put('eyes', [mp.eyes, 0], mx + ea[0], my + ea[1]);
  // the pile going over: the last three bags are the tip sprite. It leans with its foot on the bags that stay, slides half way down,
  // and the heap lies in the snow (its foot on layout.ground), beside him, never over him
  if (o.tip) {
    const k = after < 2 ? 0 : after < 4 ? 1 : 2, [fx, fy] = ANCHORS.tip.foot, s = o.tip.side || 1;
    const top = bottomAfter(o.landed, standing), heapX = clamp(L.pile[0] + s * HEAP_SIDE - fx, 0, 180 - ANCHORS.tip.size[0]);
    const x = [L.pile[0] - fx, (L.pile[0] - fx + heapX) / 2, heapX][k], foot = [top, (top + L.ground) / 2, L.ground][k];
    put('tip', [o.tip.side < 0 ? 'tip-left' : 'tip-right', k], x, foot - fy);
    if (after >= 3) {
      put('bits', ['can-' + (1 + (after & 1)), 0], heapX + (s > 0 ? 54 : 8), L.ground - 8);
      put('bits', ['loaf-1', 0], heapX + (s > 0 ? 62 : 0), L.ground - 7);
    }
  }
  // the bits that spill off an overhang: one a bag, stepping away from the bag and down
  for (const { b, since, top, i } of bits) {
    if (reduced || o.tip) continue;
    const s = b.side, x = L.pile[0] + b.c + s * ((b.w >> 1) + 2 * since) - 4, y = top + 4 + 3 * since + ((since * since) >> 1);
    if (y < L.ground - 8) put('bits', [BITS[i % 3] + '-' + (1 + (since & 1)), 0], x, y);
  }
  // the stamps: LINE! beside the pile when bag 6 lands, STILL HOLDING! up by the lamp at the bell
  if (o.lineStep >= 0) { const k = now - 1 - o.lineStep; if (k >= 0 && k < 15) put('linestamp', [k < 1 ? 'stamp-in' : 'settle', 0], 120, lineY - 30); }
  if (!o.tip && o.t >= STEPS) put('still', [after < 1 ? 'stamp-in' : 'settle', 0], L.pile[0] - ANCHORS.still.size[0] / 2, 4);
  // the hook on its rope, and the bag under it
  const h = o.hang;
  if (h) {
    const dx = clamp(h.x - 1, ANCHORS.rope.offsets[0], ANCHORS.rope.offsets[1]), hx = L.pulley[0] + dx, A = ANCHORS.bags[h.kind];
    const next = h.path[h.u + 1], way = h.auto || next === undefined ? 0 : Math.sign(next - h.x);
    put('rope', ['dx' + dx, 0], L.pulley[0] - ANCHORS.rope.top[0], L.pulley[1]);
    put('hook', 0, hx - 2, L.hookY);
    put(h.kind, [way < 0 ? 'swing-left' : way > 0 ? 'swing-right' : 'hang', 0], L.pile[0] + h.x - A.hook, L.hookY + ANCHORS.hook.holds[1]);
  }
  return ops;
}

export function drawHolden(g, o, { reduced = false, after = 0, tell = 'human' } = {}) {
  g.clearRect?.(0, 0, 180, 300);
  const ops = holdenScene(o, { reduced, after, tell });
  for (const op of ops) {
    const s = ART[op.art];
    const i = Array.isArray(op.frame) ? frameOf(s, op.frame[0], op.frame[1]) : op.frame;
    blit(g, s, i, op.x, op.y);
  }
  return ops; // what was copied where (the page keeps it for its own checks)
}
